export interface Labels {
    adaptiveHero: {
        live: string;
        matchStarting: string;
        kicksOffIn: string;
        matchCentre: string;
        days: string;
        hours: string;
        minutes: string;
        seconds: string;
        fullTime: string;
        nextMatch: string;
        lastMatch: string;
        followingMatch: string;
        welcomeTitle: string;
    };
    promoCountdown: {
        days: string;
        hours: string;
        minutes: string;
        seconds: string;
        /** Short day suffix for the inline style, e.g. "d" in "2d 04:13:22" */
        daysShort: string;
    };
    squadPage: {
        title: string;
        tabs: {
            players: string;
            staff: string;
        };
        sections: {
            GK: string;
            DEF: string;
            MID: string;
            FWD: string;
        };
        coachingStaff: string;
        headCoach: string;
        assistantCoach: string;
        staffRoles: {
            COACH: string;
            ASSISTANT_COACH: string;
            FITNESS_COACH: string;
            GOALKEEPERS_COACH: string;
            ANALYST: string;
            DOCTOR: string;
            PHYSIOTHERAPIST: string;
            MASSEUR: string;
            KIT_MANAGER: string;
            ADMINISTRATOR: string;
            TEAM_MANAGER: string;
            TRANSLATOR: string;
            VIDEOGRAPHER: string;
        };
    };
    matchesPage: {
        tabs: {
            upcoming: string;
            results: string;
        };
        filters: {
            allCompetitions: string;
            allSeasons: string;
        };
    };
    standings: {
        title: string;
        // Tab labels
        tabs: {
            liveTable: string;
            formTables: string;
            stats: string;
        };
        // Empty state
        noStandings: string;
        noStandingsDescription: string;
        // Table column headers
        table: {
            team: string;
            played: string;
            won: string;
            drawn: string;
            lost: string;
            goalsFor: string;
            goalsAgainst: string;
            goalDifference: string;
            points: string;
            form: string;
            group: string;
        };
        // Form tables
        form: {
            form: string;
            formLast8: string;
            homeFormLast8: string;
            awayFormLast8: string;
            offence: string;
            homeOffence: string;
            awayOffence: string;
            defence: string;
            homeDefence: string;
            awayDefence: string;
        };
        // Stats tab
        stats: {
            seasonProgress: string;
            matches: string;
            complete: string;
            matchOutcomes: string;
            homeWins: string;
            draws: string;
            awayWins: string;
            totalGoals: string;
            goalsByHalf: string;
            firstHalfVsSecondHalf: string;
            goalsByTimeInterval: string;
            min: string;
            goalsOverview: string;
            homeVsAwayGoalsPerMatch: string;
            average: string;
            goalsPerMatch: string;
            overUnderGoals: string;
            over1_5: string;
            over2_5: string;
            over3_5: string;
            firstGoal: string;
            homeVsAway: string;
            bothTeamsScored: string;
        };
        // Qualification / relegation / play-off zone labels — keyed by the
        // rule key the Sports API returns on each standings entry. The set is
        // open (the API can introduce new keys at any time), so this is a
        // loose dictionary. The canonical catalogue lives in `en.json`; missing
        // keys fall through to the raw key + a gray zone color at render time.
        rules: Record<string, string>;
    };
    comingSoon: {
        headline: string;
        subtext: string;
    };
    common: {
        readMore: string;
        share: string;
        search: string;
        loading: string;
        noResults: string;
        backTo: string;
        seeAll: string;
        close: string;
        today: string;
        tomorrow: string;
        vs: string;
    };
    search?: {
        title?: string;
        placeholder?: string;
        initialPrompt?: string;
        try?: string;
        tryExample1?: string;
        tryExample2?: string;
        tryExample3?: string;
        noResults?: string;
        teams?: string;
        players?: string;
        competitions?: string;
        articles?: string;
        topResults?: string;
        latestArticles?: string;
        seeAllFor?: string;
        loadMore?: string;
        showing?: string;
        retry?: string;
        forQuery?: string;
        countSummary?: string;
    };
    teams: {
        title: string;
        players: string;
        matches: string;
        stats: string;
        loading: string;
        details: string;
        schedule: string;
        clubSite: string;
    };
    matches: {
        title: string;
        upcoming: string;
        live: string;
        liveNow: string;
        finished: string;
        kickoff: string;
        round: string;
        matchday: string;
        week: string;
        roundOf: string; // "Round {current} of {total}"
        noRoundsAvailable: string;
        noMatchesInRound: string;
        loadingMatches: string;
        latestResult: string;
        nextMatch: string;
        viewAll: string;
        noMatchFound: string;
        noLatestResultTitle: string;
        noLatestResultSubtitle: string;
        noNextMatchTitle: string;
        noNextMatchSubtitle: string;
        matchDetails: string;
    };
    nav: {
        home: string;
        competitions: string;
        teams: string;
        articles: string;
        videos: string;
    };
    competitions: {
        popularCompetitions: string;
        allByCountry: string;
        searchPlaceholder: string;
        noResults: string;
        standings: string;
        matches: string;
        teams: string;
        news: string;
        draw: string;
        videos: string;
        noDraw: string;
        /** Sub-page H1s; each supports "{competitionName}" interpolation */
        pageHeadings: {
            news: string;
            videos: string;
            standings: string;
            matches: string;
            teams: string;
            draw: string;
        };
        types: {
            league: string;
            cup: string;
            tournament: string;
            friendly: string;
        };
    };
    nationsLeague: {
        groupStage: string;
        matchdayOf: string;
        knockoutComingSoon: string;
        moreFromTournament: string;
        seoTitleSuffix: string;
        seoDescription: string;
        tiers: {
            a: string;
            b: string;
            c: string;
            d: string;
        };
    };
    errors: {
        notFound: string;
        notFoundSorry: string;
        goBackHome: string;
        generic: string;
    };
    articlePage: {
        relatedArticles: string;
        latestFromCategory: string;
        latestNews: string;
        about: string;
        relatedMatches: string;
        author: string;
        authors: string;
        updatedOn: string;
    };
    authorsPage: {
        title: string;
        subtitle: string;
        noAuthors: string;
    };
    authorPage: {
        articlesByAuthor: string;
        biography: string;
        socialLinks: string;
    };
    category: {
        pageIndicator: string;
        emptyState: string;
        emptyStateVideos?: string;
        filters?: {
            all: string;
            articles: string;
            videos: string;
        };
    };
    challengeZone: {
        title: string;
        gamesAvailable: string;
        voteNow: string;
        playNow: string;
        hot: string;
        votes: string;
        played: string;
    };
    homePage: {
        // Section titles
        latestNews: string;
        featured: string;
        latest: string;
        teams: string;
        latestResults: string;
        latestResultsMobile: string;
        upcomingMatches: string;
        upcomingMatchesMobile: string;
        featuredMatches: string;
        seasonStats: string;
        standings: string;
        // Links
        viewAll: string;
        allTeams: string;
        allFixtures: string;
        allFixturesMobile: string;
        fullTable: string;
        // Season stats
        seasonProgress: string;
        matchesPlayed: string;
        toPlay: string;
        matches: string;
        totalWins: string;
        homeWins: string;
        awayWins: string;
        draws: string;
        goalsPerMatch: string;
        totalGoals: string;
        mostWins: string;
        // Match predictor promo
        matchPredictor: string;
        // Welcome bonus
        officialBettingPartner: string;
        termsApply: string;
        // Empty states
        noNewsAvailable: string;
        noVideosAvailable: string;
        noRecentResults: string;
        noUpcomingMatches: string;
        // Leaders section
        leaders: string;
        topScorers: string;
        topAssisters: string;
        goalContributions: string;
        contributions: string;
        // News portal sections
        videos: string;
        moreNews: string;
        moreArticles: string;
        // Hero section
        featuredContent: string;
    };
    // Common stat labels used across components
    stats: {
        goals: string;
        assists: string;
    };
    teamPage: {
        // Tab labels
        tabs: {
            overview: string;
            matches: string;
            squad: string;
            stats: string;
            club: string;
            news: string;
            fans: string;
        };
        // Empty states and loading
        loading: string;
        loadingSquad: string;
        noFormData: string;
        noStatsAvailable: string;
        noResultsData: string;
        // Form component labels
        form: {
            recentForm: string;
            teamForm: string;
            teamHomeForm: string;
            teamAwayForm: string;
            win: string;
            draw: string;
            loss: string;
            vs: string;
        };
        // Season stats labels
        seasonStats: {
            title: string;
            played: string;
            won: string;
            drawn: string;
            lost: string;
            winPercentage: string;
            tablePosition: string;
            points: string;
            goalsScored: string;
            goalsConceded: string;
            goalDifference: string;
            avgGoalsScored: string;
            avgGoalsAllowed: string;
        };
        // Squad position groups
        positions: {
            goalkeepers: string;
            defenders: string;
            midfielders: string;
            forwards: string;
        };
        /** Short position codes used on badges/chips (e.g. GK/DEF/MID/FWD or localized form). */
        positionsAbbr: {
            gk: string;
            def: string;
            mid: string;
            fwd: string;
        };
        // Fixtures tab
        fixtures: {
            fixtures: string;
            results: string;
            resultsTable: string;
            noUpcomingFixtures: string;
            noPastResults: string;
            noResultsData: string;
        };
        // Stats tab
        stats: {
            keyMetrics: string;
            attacking: string;
            defensive: string;
            playerStats: string;
            other: string;
            // Key metrics
            goals: string;
            assists: string;
            totalGoals: string;
            totalAssists: string;
            expectedGoals: string;
            averageRating: string;
            // Attacking stats
            attackingOverview: string;
            goalContributionsByHalf: string;
            firstVsSecondHalf: string;
            shooting: string;
            goalAttempts: string;
            shotsOnTarget: string;
            shotsOffTarget: string;
            blockedShots: string;
            shotsOnTargetByHalf: string;
            // Defensive stats
            defensiveActions: string;
            saves: string;
            interceptedCrosses: string;
            defensiveErrors: string;
            airChallenges: string;
            airChallengesWon: string;
            wonVsLost: string;
            // Possession & passing
            possessionPassing: string;
            avgPossession: string;
            passes: string;
            totalPasses: string;
            crosses: string;
            keyPasses: string;
            corners: string;
            totalCorners: string;
            possessionByHalf: string;
            cornersByHalf: string;
            // Discipline
            discipline: string;
            disciplineOverview: string;
            yellowCards: string;
            redCards: string;
            foulsCommitted: string;
            offsides: string;
            // Other stats
            otherStats: string;
            freeKicks: string;
            goalKicks: string;
            throwIns: string;
            // Player stats tables
            topScorers: string;
            topAssisters: string;
            mostAppearances: string;
            mostMinutesPlayed: string;
            mostCleanSheets: string;
            mostDecorated: string;
            player: string;
            apps: string;
            minutes: string;
            cleanSheets: string;
            cards: string;
            // Abbreviations
            avg: string;
        };
        // Player positions (for translating API position strings)
        playerPositions: {
            goalkeeper: string;
            defender: string;
            midfielder: string;
            forward: string;
            striker: string;
            winger: string;
            attacker: string;
            centreback: string;
            leftback: string;
            rightback: string;
            defensivemidfielder: string;
            centralmidfielder: string;
            attackingmidfielder: string;
            leftmidfielder: string;
            rightmidfielder: string;
            leftwinger: string;
            rightwinger: string;
        };
        // Leaderboard
        leaderboard: {
            title: string;
            predictorTitle: string;
            topxTitle: string;
            fantasyTitle: string;
            fullRankings: string;
            fan: string;
            points: string;
            you: string;
            noEntries: string;
            noEntriesSubtitle: string;
            errorLoading: string;
            errorSubtitle: string;
        };
        // Club info
        clubInfo: {
            title: string;
            nickname: string;
            country: string;
            founded: string;
            stadium: string;
            manager: string;
            capacity: string;
            socials: string;
        };
        // Featured players
        featuredPlayers: {
            title: string;
            viewAll: string;
            roles: {
                def: string;
                mid: string;
                fwd: string;
            };
            /**
             * Stat labels used after a count on the signature-stat tile.
             * Bulgarian needs the count form ("15 Гола", not "15 Голове");
             * most other languages use the same string as the base label.
             */
            signatureStat: {
                cleanSheets: string;
                goalsPlusAssists: string;
                goals: string;
            };
        };
        // News
        news: {
            latestNews: string;
            loadMore: string;
        };
        // Fans
        fans: {
            fanZone: string;
            tabs: {
                latest: string;
                quizzes: string;
                polls: string;
            };
            predictor: {
                title: string;
                description: string;
                ctaTitle: string;
                ctaDescription: string;
                ctaButton: string;
                feature1: string;
                feature2: string;
                feature3: string;
            };
            miniGames: {
                title: string;
                viewAll: string;
                noGames: string;
                quiz: string;
                eitherOr: string;
                personalityQuiz: string;
            };
            polls: {
                title: string;
                viewAll: string;
                noPolls: string;
                vote: string;
                voted: string;
            };
        };
        // Gamification
        gamification: {
            playNow: string;
            castYourVote: string;
            questions: string;
            avgScore: string;
            plays: string;
            totalVotes: string;
            players: string;
            participants: string;
            votes: string;
            placeholder: string;
            signInToPlay: string;
            signInToVote: string;
        };
        // Club tab
        club: {
            clubHistory: string;
            keyAchievements: string;
            manager: string;
            stadium: string;
            clubLegends: string;
            rivalries: string;
        };
        // Results table
        resultsTable: {
            team: string;
            gp: string;
            pts: string;
            home: string;
            away: string;
        };
        // Labels describing what time period the squad player-card stats cover.
        // Shown above the squad list so users know which competitions / window
        // the numbers reflect. Optional so locales we haven't translated yet
        // still satisfy the Labels cast in loader.ts; UI falls back to the key
        // path via the safe-labels proxy.
        squadStatsScope?: {
            title: string;
            worldCup: string;
            season: string;
            last6Months: string;
        };
        // Player card stats (used in squad player cards)
        playerCardStats: {
            appearances: string;
            appsSub: string;
            starts: string;
            minutes: string;
            cleanSheets: string;
            conceded: string;
            goals: string;
            assists: string;
            pensScored: string;
            pensMissed: string;
            ownGoals: string;
            yellow: string;
            red: string;
            minsPerMatch: string;
            minsPerGoal: string;
            saves: string;
            catches: string;
            fouls: string;
            shotsOnTarget: string;
            offsides: string;
            totalGoals: string;
            totalAssists: string;
            totalCorners: string;
            captain: string;
            goalsPlusAssists: string;
            cleanSheetsPercent: string;
            yellowSlashRed: string;
        };
        /** Short column headers for the squad-page tables. Rendered inside <abbr> with the full label as title tooltip. */
        playerCardStatsAbbr: {
            app: string;
            st: string;
            min: string;
            mPerM: string;
            cs: string;
            gc: string;
            csPercent: string;
            g: string;
            a: string;
            gPlusA: string;
            ps: string;
            pm: string;
            psSlashPm: string;
            og: string;
            mPerG: string;
            cap: string;
            ycRc: string;
        };
        squadColumnPlayer: string;
        // Stats section headers
        statsHeaders: {
            keyMetrics: string;
            attackingOverview: string;
            defensiveOverview: string;
            possessionPassingOverview: string;
            disciplineOverview: string;
            goals: string;
            goalContributionsByHalf: string;
            shooting: string;
            shotsOnTargetByHalf: string;
            defensiveActions: string;
            airChallenges: string;
            possessionStatistics: string;
            possessionByHalf: string;
            corners: string;
            cornersByHalf: string;
            discipline: string;
            otherStatistics: string;
        };
    };
    playerPage: {
        tabs: {
            overview: string;
            stats: string;
            news: string;
            fans: string;
        };
        info: {
            title: string;
            age: string;
            nationality: string;
            height: string;
            weight: string;
            team: string;
            shirtNumber: string;
            preferredFoot: string;
            position: string;
            positions: {
                forward: string;
                midfielder: string;
                defender: string;
                keeper: string;
            };
            dateOfBirth: string;
        };
        seasonStats: {
            title: string;
            appearances: string;
            goals: string;
            assists: string;
            minutesPlayed: string;
            cleanSheets: string;
            yellowCards: string;
            redCards: string;
            saves: string;
            goalsConceded: string;
            fouls: string;
            shots: string;
            offsides: string;
            catches: string;
        };
        careerStats: {
            title: string;
            season: string;
            team: string;
            competition: string;
            appearances: string;
            minutes: string;
            goals: string;
            assists: string;
            yellowCards: string;
            redCards: string;
            cleanSheets: string;
            shots: string;
            offsides: string;
            totals: string;
            all: string;
            club: string;
            international: string;
        };
        recentForm: {
            title: string;
            opponent: string;
            result: string;
            mins: string;
            noRecentMatches: string;
            aggregateLabel: string;
            appearances: string;
            goals: string;
            assists: string;
            minutes: string;
            yellowCards: string;
            redCards: string;
        };
        stats: {
            seasonTab: string;
            careerTab: string;
            attacking: string;
            defensive: string;
            discipline: string;
            passing: string;
            general: string;
            noStats: string;
        };
        news: {
            latestNews: string;
            noArticles: string;
        };
        fans: {
            fanZone: string;
            noGames: string;
            noPolls: string;
        };
        matches: {
            lastAppearance: string;
            nextFixture: string;
        };
    };
    pageMetadata: {
        home: {
            title: string;
            description: string;
        };
        search?: {
            title: string;
            description: string;
        };
        competition: {
            title: string;
            description: string;
        };
        team: {
            title: string;
            description: string;
        };
        player: {
            title: string;
            description: string;
        };
        match: {
            title: string;
            description: string;
            statusResult: string;
            statusLive: string;
            statusPreview: string;
        };
        competitionsList: {
            title: string;
            description: string;
        };
        footballCompetitions: {
            title: string;
            description: string;
        };
        footballCompetition: {
            title: string;
            description: string;
        };
        teamsList: {
            title: string;
            description: string;
        };
        matchesList: {
            title: string;
            description: string;
        };
        standings: {
            title: string;
            description: string;
        };
        squad?: {
            title: string;
            description: string;
        };
        teamMatches?: {
            title: string;
            description: string;
        };
        teamStandings?: {
            title: string;
            description: string;
        };
        competitionNews: {
            title: string;
            description: string;
        };
        competitionVideos: {
            title: string;
            description: string;
        };
        competitionDraw: {
            title: string;
            description: string;
        };
        predictor: {
            title: string;
            description: string;
        };
        articles: {
            title: string;
            description: string;
        };
        article: {
            title: string;
            description: string;
        };
        authors: {
            title: string;
            description: string;
        };
        author: {
            title: string;
            description: string;
        };
        category: {
            title: string;
            description: string;
        };
        customPage: {
            title: string;
            description: string;
        };
        livescore: {
            title: string;
            description: string;
        };
        vote: {
            title: string;
            description: string;
        };
        players: {
            title: string;
            description: string;
        };
        video: {
            title: string;
            description: string;
        };
        videos: {
            title: string;
            description: string;
        };
        // Authentication pages
        signIn: {
            title: string;
            description: string;
        };
        signUp: {
            title: string;
            description: string;
        };
        forgotPassword: {
            title: string;
            description: string;
        };
        profile: {
            title: string;
            description: string;
        };
    };
    matchPage: {
        // Tab labels
        tabs: {
            events: string;
            lineups: string;
            commentary: string;
            stats: string;
            odds: string;
            form: string;
            standings: string;
            h2h: string;
            news: string;
        };
        // News tab labels
        newsTab: {
            matchCoverage: string;
            teamNews: string; // "Latest news for {teamName}" - use {teamName} as placeholder
        };
        // Match status labels
        status: {
            ft: string; // Full Time (short)
            ftFull: string; // Full Time (full label for tooltip)
            aet: string; // After Extra Time (short)
            aetFull: string; // After Extra Time (full label for tooltip)
            ap: string; // After Penalties (short)
            apFull: string; // After Penalties (full label for tooltip)
        };
        h2hTab: {
            headToHeadRecord: string;
            recentMeetings: string;
            wins: string;
            goalsScored: string;
            avgGoalsPerMatch: string;
            matchesPlayed: string;
            draws: string;
            noH2HMatches: string;
        };
        formTab: {
            awaitingFirstMatch: string;
        };
        statsTab: {
            matchStats: string;
            // Stat keys from the Sports Events API are open-ended; the index
            // signature lets new keys land in the locale JSONs without a
            // breaking type churn.
            [key: string]: string | undefined;
        };
        resultsTab: {
            results: string;
            streaksAndSequences: string;
            goalStatistics: string;
            homeVsAwayStats: string;
            home: string;
            away: string;
            total: string;
            opponent: string;
            goalsScored: string;
            gsPerMatch: string;
            goalsConceded: string;
            gcPerMatch: string;
            gsGcPerMatch: string;
            over05FT: string;
            over15FT: string;
            over25FT: string;
            over35FT: string;
            over45FT: string;
            over55FT: string;
            over05HT: string;
            over15HT: string;
            over25HT: string;
            played: string;
            goalsScoredPerMatch: string;
            cleanSheets: string;
            wonToNil: string;
            scoringRate: string;
            scoredInBothHalves: string;
            scoredFirst: string;
            leadingAtHalfTime: string;
            goalsConcededPerMatch: string;
            failedToScore: string;
            lostToNil: string;
            concedingRate: string;
            concededInBothHalves: string;
            concededFirst: string;
            losingAtHalfTime: string;
            // Streak labels (translated from library)
            streakConsecutiveWins: string;
            streakConsecutiveDraws: string;
            streakConsecutiveDefeats: string;
            streakNoWin: string;
            streakNoDraw: string;
            streakNoDefeat: string;
            streakScoredAtLeastOnce: string;
            streakConcededAtLeastOnce: string;
            streakNoGoalScored: string;
            streakNoGoalConceded: string;
            streakOver25: string;
            streakUnder25: string;
            streakScoredAtLeastTwice: string;
            streakConcededAtLeastTwice: string;
        };
        // Lineups
        lineups: {
            substitutes: string;
            substitutions: string;
            positions: {
                keeper: string;
                defender: string;
                midfielder: string;
                forward: string;
            };
        };
        // Commentary
        commentary: string;
        // Match Events
        events: {
            title: string;
            halfTime: string;
            assist: string;
            // Event types
            goal: string;
            penalty: string;
            penaltyScored: string;
            ownGoal: string;
            yellowCard: string;
            redCard: string;
            secondYellow: string;
            substitution: string;
            penaltyMissed: string;
            penaltyAwarded: string;
            event: string;
        };
        // Betting odds
        odds: {
            // Market titles
            markets: {
                matchResult: string;
                whoWillWin: string;
                doubleChance: string;
                drawNoBet: string;
                halfTimeFullTime: string;
                halfTimeMarkets: string;
                halfTime1x2: string;
                halfTimeOverUnderGoals: string;
                bothTeamsToScore: string;
                overUnderGoals: string;
                overUnderGoalsLine: string;
                correctScore: string;
                asianHandicap: string;
                firstTeamToScore: string;
                corners: string;
            };
            // Selection labels for First Team to Score
            selectionsFirstTeamToScore: {
                home: string;
                none: string;
                away: string;
            };
            // Selection labels for 1X2
            selections1x2: {
                home: string;
                draw: string;
                away: string;
            };
            // Selection labels for Double Chance
            selectionsDoubleChance: {
                homeOrDraw: string;
                homeOrAway: string;
                drawOrAway: string;
            };
            // Selection labels for Both Teams to Score
            selectionsBtts: {
                yes: string;
                no: string;
            };
            // Selection labels for Over/Under
            selectionsOverUnder: {
                over: string;
                under: string;
            };
            // HT/FT labels
            selectionsHtft: {
                homeHome: string;
                homeDraw: string;
                homeAway: string;
                drawHome: string;
                drawDraw: string;
                drawAway: string;
                awayHome: string;
                awayDraw: string;
                awayAway: string;
            };
            // Common
            noOddsAvailable: string;
            moreOdds: string;
            predictExactScore: string;
            predictHtFt: string;
            willBothTeamsScore: string;
        };
    };
    auth: {
        signIn: {
            title: string;
            subtitle: string;
            email: string;
            password: string;
            signingIn: string;
            signInButton: string;
            orContinueWith: string;
            continueWithGoogle: string;
            signingInWithGoogle: string;
            forgotPassword: string;
            noAccount: string;
            signUpLink: string;
        };
        signUp: {
            title: string;
            subtitle: string;
            name: string;
            email: string;
            password: string;
            confirmPassword: string;
            passwordMismatch: string;
            passwordTooShort: string;
            creatingAccount: string;
            createAccountButton: string;
            orContinueWith: string;
            continueWithGoogle: string;
            signingInWithGoogle: string;
            hasAccount: string;
            signInLink: string;
        };
        forgotPassword: {
            title: string;
            subtitle: string;
            email: string;
            sending: string;
            sendButton: string;
            emailSent: string;
            emailSentDescription: string;
            backToSignIn: string;
        };
        profile: {
            title: string;
            subtitle: string;
            settings: string;
            editProfile: string;
            editPassword: string;
            changeAvatar: string;
            avatarHint: string;
            avatarInvalidType: string;
            avatarTooLarge: string;
            avatarUpdated: string;
            avatarUploadError: string;
            selectImage: string;
            uploadAvatar: string;
            uploading: string;
            chooseAnother: string;
            loadingProfile: string;
            tryAgain: string;
            profileUpdated: string;
            name: string;
            email: string;
            displayName: string;
            userId: string;
            nickname: string;
            nicknamePlaceholder: string;
            nicknameHint: string;
            nicknameValidationError: string;
            nicknameAlreadyTaken: string;
            noNickname: string;
            country: string;
            selectCountry: string;
            gender: string;
            selectGender: string;
            male: string;
            female: string;
            ratherNotSay: string;
            saveChanges: string;
            saving: string;
            saveError: string;
            reset: string;
            currentPassword: string;
            newPassword: string;
            confirmPassword: string;
            passwordRequirements: string;
            changePassword: string;
            changingPassword: string;
            passwordChanged: string;
            passwordChangeError: string;
            passwordMismatch: string;
            passwordTooShort: string;
            wrongPassword: string;
            weakPassword: string;
            requiresRecentLogin: string;
            strong: string;
            good: string;
            weak: string;
            signOut: string;
            signingOut: string;
            signOutDescription: string;
            anonymous: string;
            dangerZone: string;
            dangerZoneDescription: string;
            deleteAccount: string;
            deleteAccountDescription: string;
            confirmDeleteTitle: string;
            deleteConfirmation: string;
            deleteWarning: string;
            deleteConfirmationLabel: string;
            deleteConfirmationPlaceholder: string;
            deleteConfirmationError: string;
            deleteError: string;
            confirmDelete: string;
            deleting: string;
            cancel: string;
        };
        myActivity: {
            title: string;
            subtitle: string;
            loading: string;
            points: string;
            currentTier: string;
            nextTier: string;
            maxTierReached: string;
            tierProgress: string;
            predictionsMade: string;
            winRate: string;
            totalPoints: string;
            badgesEarned: string;
            badges: string;
            earned: string;
            locked: string;
            achievedOn: string;
            noBadges: string;
            categoryPredictor: string;
            categoryGames: string;
            categoryFootball: string;
            categoryDiscussions: string;
            categoryVoting: string;
            categoryTopX: string;
            categoryMatchQuiz: string;
            categoryClassicQuizzes: string;
            categoryEitherOr: string;
            predictionStats: string;
            overallSuccessRate: string;
            byMarket: string;
            singlePredictions: string;
            correct: string;
            topXGames: string;
            matchQuizGames: string;
            participations: string;
            predictionHistory: string;
            noPredictions: string;
            noPredictionsDescription: string;
            loadMore: string;
            pointsEarned: string;
            won: string;
            lost: string;
            active: string;
            partiallyWon: string;
            canceled: string;
            discussionStats: string;
            postsMade: string;
            reactionsReceived: string;
            discussionPoints: string;
            marketFT1X2: string;
            marketHT1X2: string;
            marketHTFT: string;
            marketCorrectScore: string;
            marketCorrectScoreHT: string;
            marketCorrectScoreAdvanced: string;
            marketBTTS: string;
            marketOverUnder: string;
            marketDoubleChance: string;
            marketPlayerGoalscorer: string;
        };
        controls: {
            myProfile: string;
            signOut: string;
            logIn: string;
            signUp: string;
        };
        errors: {
            invalidCredentials: string;
            emailInUse: string;
            invalidEmail: string;
            weakPassword: string;
            tooManyRequests: string;
            network: string;
            userDisabled: string;
            popupBlocked: string;
            generic: string;
        };
    };
    matchPredictionCard: {
        // Front side (CTA)
        badge: string;
        predictionsCloseIn: string;
        ctaButton: string;
        tagline: string;
        // Countdown units
        days: string;
        hours: string;
        minutes: string;
        seconds: string;
        // Back side (Form)
        formTitle: string;
        predictionError: string;
        viewFullGame: string;
        backButton: string;
        submitButton: string;
        submitting: string;
        // Empty state
        noUpcomingMatch: string;
    };
    predictorPage: {
        title: string;
        subtitle: string;
        tabs: {
            play: string;
            privateLeagues: string;
            leaderboard: string;
            rules: string;
            prizes: string;
        };
        play: {
            noGroupsAvailable: string;
            noMatchesInGroup: string;
            groupOf: string;
        };
        leaderboard: {
            title: string;
            subtitle: string;
            player: string;
            points: string;
            you: string;
            anonymous: string;
            noDataAvailable: string;
            previous: string;
            next: string;
            pageOf: string;
        };
        rules: {
            title: string;
            subtitle: string;
            noRulesConfigured: string;
        };
        prizes: {
            title: string;
            subtitle: string;
            noPrizesConfigured: string;
        };
        privateLeagues: {
            title: string;
            subtitle: string;
            createLeague: {
                title: string;
                description: string;
                button: string;
            };
            joinLeague: {
                title: string;
                description: string;
                placeholder: string;
                button: string;
            };
            myLeagues: string;
            members: string;
            yourPosition: string;
            points: string;
            viewLeague: string;
            positionSuffix: {
                st: string;
                nd: string;
                rd: string;
                th: string;
            };
        };
        leagueDetail: {
            backToLeagues: string;
            leagueCode: string;
            copyCode: string;
            copied: string;
            createdBy: string;
            members: string;
            tabs: {
                leaderboard: string;
                comments: string;
            };
        };
        leagueLeaderboard: {
            player: string;
            points: string;
            you: string;
            noPredictions: string;
        };
        odds: {
            draw: string;
            toWin: string;
        };
    };
    prediction: {
        makePrediction: string;
        predict: string;
        submitting: string;
        predictionSaved: string;
        predictionError: string;
        edit: string;
        yourPrediction: string;
        finalResult: string;
        correct: string;
        partiallyCorrect: string;
        incorrect: string;
        pts: string;
        noPrediction: string;
        draw: string;
        over: string;
        under: string;
        yes: string;
        no: string;
        bothTeamsToScore: string;
    };
    signInPrompt: {
        title: string;
        description: string;
        benefit1: string;
        benefit2: string;
        benefit3: string;
        benefit4: string;
        signInButton: string;
        signUpButton: string;
    };
    livescore: {
        title: string;
        sports: {
            title?: string;
            football: string;
            basketball: string;
            tennis: string;
            hockey: string;
            americanFootball: string;
            "american-football"?: string;
        } & { [K in "football" | "basketball" | "tennis" | "hockey" | "american-football"]?: string };
        filters: {
            all: string;
            popular: string;
            upcoming: string;
            live: string;
            finished: string;
            odds: string;
            myLeagues: string;
        };
        noMatches: string;
        matchesCount: string;
        liveCount: string;
        expandCompetition: string;
        collapseCompetition: string;
        moreCompetitionsLoading: string;
        boardUnavailable: string;
        standings: string;
        loading: string;
        ft: string;
        fin: string;
        ht: string;
        pen: string;
        paused: string;
        quarter: string;
        set: string;
        period: string;
        total: string;
        showMoreCompetitions: string;
        clearCompetitionFilter: string;
        picker: {
            title?: string;
            save: string;
            searchPlaceholder: string;
            addLeaguesHint: string;
            noFavourites: string;
            addYourLeagues: string;
        };
    };
    betslip: {
        title: string;
        empty: string;
        emptyHint: string;
        emptyTitle?: string;
        emptyDescription?: string;
        continueLabel?: string;
        disclaimer?: string;
        combinedOdds?: string;
        totalStake?: string;
        oddsUnavailable?: string;
        suspended?: string;
        placeBet?: string;
        yourSelection: string;
        selectionCount: string;
        removeSelection: string;
        clearAll: string;
        clearAllConfirm: string;
        ctaButton: string;
        expandLabel: string;
        maxSelectionsReached: string;
        oddsSuspended: string;
        predictionLabel: string;
        oddsLabel: string;
        totalOdds: string;
        stake: string;
        potentialWin: string;
        market_FT_1X2: string;
        outcome_1: string;
        outcome_X: string;
        outcome_2: string;
        bothTeamsToScore: string;
        bothTeamsNotToScore: string;
        correctScorePrefix: string;
        goals: string;
    };
    worldCup: {
        title: string;
        dateRange: string;
        days: string;
        hours: string;
        mins: string;
        secs: string;
        liveMessage: string;
        viewMatches: string;
    };
    blocks: {
        competitorCard: {
            viewProfile: string;
            seasonStatistics: string;
            recentForm: string;
            nextMatch: string;
            matchOdds: string;
        };
    };
    consent: {
        banner: {
            title: string;
            description: string;
        };
        acceptAll: string;
        rejectAll: string;
        managePreferences: string;
        save: string;
        categories: {
            essential: {
                title: string;
                description: string;
            };
            analytics: {
                title: string;
                description: string;
            };
            marketing: {
                title: string;
                description: string;
            };
        };
        footerLink: string;
    };
    profileConsent: {
        required: string;
        optional: string;
        acceptAndContinue: string;
        cancel: string;
        gateTitle: string;
        gateDescription: string;
        settingsTitle: string;
        settingsDescriptionOptional: string;
        settingsRequiredNotice: string;
        deleteAccountLink: string;
        loadFailed: string;
    };
    players: {
        title: string;
        emptyState: string;
        categories: {
            topScorers: string;
            topAssisters: string;
            topContributors: string;
        };
        stats: {
            goals: string;
            assists: string;
            contributions: string;
        };
    };
    localeSelector: {
        title: string;
    };
    sportEventPage: {
        tabs: {
            overview: string;
            stats: string;
            form: string;
            h2h: string;
            standings: string;
            lineups: string;
        };
        status: {
            live: string;
            ft: string;
        };
        competitorFallbackOne: string;
        competitorFallbackTwo: string;
        scoreBreakdown: {
            title: string;
            total: string;
            totalLong: string;
            winner: string;
            period: string;
            quarter: string;
            set: string;
            periodShort: string;
            quarterShort: string;
            setShort: string;
            overtime: string;
            overtimeNumbered: string;
            penalties: string;
        };
        mainEvents: {
            title: string;
        };
        stats: {
            matchStats: string;
            recentFormIndicators: string;
            keys: Record<string, string>;
        };
        form: {
            title: string;
            home: string;
            away: string;
            homeLong: string;
            awayLong: string;
            noData: string;
            outcomes: {
                w: string;
                d: string;
                l: string;
                wLong: string;
                dLong: string;
                lLong: string;
            };
        };
        h2h: {
            title: string;
            recentMeetings: string;
            noData: string;
        };
        standings: {
            title: string;
            team: string;
            headers: Record<string, string>;
        };
        lineups: {
            title: string;
            starting: string;
            substitutes: string;
            coach: string;
        };
    };
    tv: {
        watchOn: string;
        seeAllBroadcasters: string;
        notTelevisedIn: string; // supports "{country}" interpolation
        searchBroadcasters: string;
        linearTv: string;
        streaming: string;
        countryOne: string; // supports "{count}" interpolation
        countryOther: string; // supports "{count}" interpolation
    };
    pagination: {
        first: string;
        previous: string;
        next: string;
        last: string;
        /** Accessible name of the pagination <nav> landmark */
        ariaLabel: string;
    };
    countries: Record<string, string>; // ISO 3166-1 alpha-2 → display name
}

// Internal locale codes (BCP 47)
export type LocaleCode =
    | "en"
    | "es"
    | "pt-BR"
    | "pt"
    | "bg"
    | "el"
    | "de"
    | "fr"
    | "it"
    | "ro"
    | "th"
    | "tr"
    | "vi"
    | "zh"
    | "ko"
    | "da"
    | "cs"
    | "sq"
    | "nl";

// URL slugs (what appears in the URL for multi-lang tenants)
export type LocaleSlug =
    | "en"
    | "es"
    | "pt-br" // Brazilian Portuguese
    | "pt" // European Portuguese
    | "bg"
    | "el"
    | "de"
    | "fr"
    | "it"
    | "ro"
    | "th"
    | "tr"
    | "vi"
    | "zh"
    | "ko"
    | "da"
    | "cs"
    | "sq"
    | "nl";

export const SUPPORTED_LOCALES: LocaleCode[] = [
    "en",
    "es",
    "pt-BR",
    "pt",
    "bg",
    "el",
    "de",
    "fr",
    "it",
    "ro",
    "th",
    "tr",
    "vi",
    "zh",
    "ko",
    "da",
    "cs",
    "sq",
    "nl",
];

// URL slug → Internal locale code
export const SLUG_TO_LOCALE: Record<LocaleSlug, LocaleCode> = {
    en: "en",
    es: "es",
    "pt-br": "pt-BR",
    pt: "pt",
    bg: "bg",
    el: "el",
    de: "de",
    fr: "fr",
    it: "it",
    ro: "ro",
    th: "th",
    tr: "tr",
    vi: "vi",
    zh: "zh",
    ko: "ko",
    da: "da",
    cs: "cs",
    sq: "sq",
    nl: "nl",
};

// Internal locale → URL slug
export const LOCALE_TO_SLUG: Record<LocaleCode, LocaleSlug> = {
    en: "en",
    es: "es",
    "pt-BR": "pt-br",
    pt: "pt",
    bg: "bg",
    el: "el",
    de: "de",
    fr: "fr",
    it: "it",
    ro: "ro",
    th: "th",
    tr: "tr",
    vi: "vi",
    zh: "zh",
    ko: "ko",
    da: "da",
    cs: "cs",
    sq: "sq",
    nl: "nl",
};

// Full language names (in their own language)
export const LOCALE_NAMES: Record<LocaleCode, string> = {
    en: "English",
    es: "Español",
    "pt-BR": "Português (Brasil)",
    pt: "Português",
    bg: "Български",
    el: "Ελληνικά",
    de: "Deutsch",
    fr: "Français",
    it: "Italiano",
    ro: "Română",
    th: "ไทย",
    tr: "Türkçe",
    vi: "Tiếng Việt",
    zh: "中文",
    ko: "한국어",
    da: "Dansk",
    cs: "Čeština",
    sq: "Shqip",
    nl: "Nederlands",
};

// Short display codes for language switcher
export const LOCALE_DISPLAY: Record<LocaleCode, string> = {
    en: "EN",
    es: "ES",
    "pt-BR": "BR",
    pt: "PT",
    bg: "BG",
    el: "EL",
    de: "DE",
    fr: "FR",
    it: "IT",
    ro: "RO",
    th: "TH",
    tr: "TR",
    vi: "VI",
    zh: "中",
    ko: "한",
    da: "DA",
    cs: "CS",
    sq: "SQ",
    nl: "NL",
};
